<script lang="ts">
import type { ButtonProps } from './Button.vue';
import { tv } from 'tailwind-variants';
import theme from '#build/ui/button';
import { pick } from '#layers/base/public';

export interface CopyButtonProps extends Omit<ButtonProps, 'icon'> {
	/**
	 * The value to copy to clipboard
	 */
	value?: string;
}

const copyButton = tv({
	base: 'transition-colors',
	variants: pick(theme.variants, ['color', 'variant']),
	compoundVariants: [
		{ color: 'neutral', variant: 'link', class: 'text-dimmed' },
	],
});
</script>

<script setup lang="ts">
import { reactiveOmit, useClipboard } from '@vueuse/core';
import { useForwardProps } from 'reka-ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAppConfig } from '#app/config';

const props = withDefaults(defineProps<CopyButtonProps>(), {
	color: 'neutral',
	variant: 'link',
});

const forwarded = useForwardProps(reactiveOmit(props, 'value'));

const { t } = useI18n();
const appConfig = useAppConfig();
const { copy: copyText, copied: textCopied } = useClipboard();

const label = computed(() => textCopied.value ? t('common.copied') : t('common.copy_to_clipboard'));
const classes = computed(() => copyButton({ color: props.color, variant: props.variant }));
</script>

<template>
	<DTooltip :text="label">
		<DButton
			v-bind="forwarded"
			:class="classes"
			:aria-label="label"
			@click="copyText(value ?? '')"
		>
			<template #leading="{ ui }">
				<span class="grid shrink-0 place-items-center">
					<Transition
						mode="out-in"
						enter-active-class="transition-[opacity,filter,transform] duration-100 ease-out motion-reduce:transition-none"
						enter-from-class="opacity-0 blur-sm scale-80"
						leave-active-class="transition-[opacity,filter,transform] duration-100 ease-out motion-reduce:transition-none"
						leave-to-class="opacity-0 blur-sm scale-80"
					>
						<DIcon
							:key="textCopied ? 'copied' : 'copy'"
							:name="textCopied ? appConfig.ui.icons.check : 'lucide:copy'"
							class="col-start-1 row-start-1"
							:class="[ui.leadingIcon()]"
						/>
					</Transition>
				</span>
			</template>
		</DButton>
	</DTooltip>
</template>
